/**
 * Sub-components + helpers for the recorded workflow plan
 * (``ComputerUseRecordedWorkflowPlan``).
 * Split out so the main component file stays under the
 * ``max-lines`` lint threshold; mirrors the
 * ``InlineClarificationBlock.parts.tsx`` split used for the same
 * reason.
 */

import type { ReactNode } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import {
  ParamChip,
  TemplateParts,
} from '@/features/rpa/recorder/components/workflow/workflow-step-template';
import { humanizeParameterName } from '@/features/rpa/recorder/lib/humanize-parameter-name';
import type {
  WorkflowParameter,
  WorkflowStep,
} from '@/features/rpa/recorder/types/workflow';
import {
  TypographyCaption,
  TypographyCaptionBold,
} from '@heidi/design-system/typography';

const parameterItemMessages = defineMessages({
  required: {
    id: 'Seanpxav9K',
    defaultMessage: 'Required',
  },
});

export const RecordedWorkflowSlugChip = ({ slug }: { slug: string }) => (
  <span className="rounded bg-background-tertiary px-1.5 py-0.5 text-[10px] font-medium text-text-tertiary">
    {formatRecordedWorkflowSlug(slug)}
  </span>
);

export const RecordedWorkflowSection = ({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) => (
  <div className="flex flex-col gap-1.5">
    <TypographyCaptionBold className="text-text-secondary">{label}</TypographyCaptionBold>
    {children}
  </div>
);

export const RecordedWorkflowParameterItem = ({
  parameter,
  value,
}: {
  parameter: WorkflowParameter;
  value: unknown;
}) => {
  const intl = useIntl();
  const label = humanizeParameterName(parameter.name);
  const displayValue = formatInputValue(value);
  return (
    <li className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-1">
      <span className="flex min-w-0 flex-wrap items-center gap-1 text-text-secondary">
        <span className="font-medium text-text-primary">{label}:</span>
        {displayValue ? (
          <ParamChip name={parameter.name}>{displayValue}</ParamChip>
        ) : (
          <ParamChip name={parameter.name} />
        )}
      </span>
      {parameter.required && (
        <span className="rounded bg-background-tertiary px-1.5 py-0.5 text-[10px] font-medium text-text-tertiary">
          {intl.formatMessage(parameterItemMessages.required)}
        </span>
      )}
    </li>
  );
};

export const RecordedWorkflowStepItem = ({
  step,
  inputValues,
}: {
  step: WorkflowStep;
  inputValues: Readonly<Record<string, unknown>>;
}) => {
  const detail = firstWorkflowStepDetail(step);
  return (
    <li className="flex min-w-0 gap-2">
      <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full border border-border/60 bg-surface text-[10px] font-medium tabular-nums text-text-secondary">
        {step.stepNumber}
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <TypographyCaptionBold className="break-words text-text-primary [overflow-wrap:anywhere]">
          <TemplateParts
            value={step.label}
            renderParameter={(parameterName) =>
              renderResolvedInputChip(parameterName, inputValues)
            }
          />
        </TypographyCaptionBold>
        {detail && (
          <TypographyCaption className="break-words text-text-tertiary [overflow-wrap:anywhere]">
            <TemplateParts
              value={detail}
              renderParameter={(parameterName) =>
                renderResolvedInputChip(parameterName, inputValues)
              }
            />
          </TypographyCaption>
        )}
      </div>
    </li>
  );
};

export const RecordedWorkflowBulletList = ({ items }: { items: readonly string[] }) => (
  <ul className="ms-4 list-disc space-y-1 marker:text-text-tertiary">
    {items.map((item, index) => (
      <li
        key={`${item}-${index}`}
        className="break-words text-text-secondary [overflow-wrap:anywhere]"
      >
        {item}
      </li>
    ))}
  </ul>
);

const firstWorkflowStepDetail = (step: WorkflowStep): string | undefined =>
  step.detailSections[0]?.value;

const renderResolvedInputChip = (
  parameterName: string,
  inputValues: Readonly<Record<string, unknown>>
): ReactNode | undefined => {
  const displayValue = formatInputValue(inputValues[parameterName]);
  return displayValue ? (
    <ParamChip name={parameterName}>{displayValue}</ParamChip>
  ) : undefined;
};

const formatRecordedWorkflowSlug = (slug: string): string => {
  const normalized = slug.trim();
  if (normalized.startsWith('/')) return normalized;
  return `/${normalized.replace(/_/g, '-')}`;
};

const formatInputValue = (value: unknown): string | undefined => {
  if (value == null) return undefined;
  if (typeof value === 'string') return value.trim() || undefined;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (typeof value === 'bigint') return value.toString();
  return undefined;
};
